import React from "react";
import { Button, Checkbox, Input, Space } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Game } from "../../../../services/gameQuiz/gameQuiz.types";
import ImageUploadInput from "../ImageUploadInput";
import { generateOptionId } from "../gameTypes";
import TtsAudioControls from "../TtsAudioControls";
import OptionRichText from "../OptionRichText";

const stripHtml = (html: string): string => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
};

type Props = {
  game: Extract<Game, { type: "multiple_choice" }>;
  onChange: (game: Extract<Game, { type: "multiple_choice" }>) => void;
  generateAudio?: (text: string) => Promise<string>;
};

const MultipleChoiceEditor: React.FC<Props> = ({
  game,
  onChange,
  generateAudio,
}) => {
  // Defensive: partial/legacy games may arrive without an options array.
  // Fall back to an empty list so the map() below doesn't crash.
  const options: typeof game.options = Array.isArray(game.options)
    ? game.options
    : [];

  const updateOption = (
    optionId: string,
    patch: Partial<(typeof game.options)[number]>,
  ) => {
    onChange({
      ...game,
      options: options.map((o) => (o.id === optionId ? { ...o, ...patch } : o)),
    });
  };

  const addOption = () => {
    onChange({
      ...game,
      options: [
        ...options,
        { id: generateOptionId(), text: "", correct: false },
      ],
    });
  };

  const removeOption = (optionId: string) => {
    onChange({
      ...game,
      options: options.filter((o) => o.id !== optionId),
    });
  };

  return (
    <div>
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <div>
          <label>Pregunta</label>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <Input.TextArea
              value={game.question}
              onChange={(e) => onChange({ ...game, question: e.target.value })}
              rows={2}
              placeholder="Escribe la pregunta"
              style={{ flex: 1 }}
            />
            <TtsAudioControls
              text={game.question}
              audioUrl={game.questionAudio}
              onAudioChange={(url) => onChange({ ...game, questionAudio: url })}
              generateAudio={generateAudio}
            />
          </div>
        </div>
        <div>
          <label>Imagen (opcional)</label>
          <ImageUploadInput
            value={game.image}
            onChange={(url) => onChange({ ...game, image: url })}
          />
        </div>
        <div>
          <label>Opciones</label>
          {options.map((option) => (
            <div
              key={option.id}
              style={{
                marginTop: 8,
                padding: 8,
                border: "1px solid var(--bo-card-border)",
                borderRadius: 6,
              }}
            >
              <Space style={{ display: "flex", alignItems: "flex-start" }}>
                <Checkbox
                  checked={option.correct}
                  onChange={(e) =>
                    updateOption(option.id, { correct: e.target.checked })
                  }
                  style={{ marginTop: 8 }}
                >
                  Correcta
                </Checkbox>
                <OptionRichText
                  value={option.text}
                  onChange={(html) => updateOption(option.id, { text: html })}
                  placeholder="Texto de la opción"
                />
                <TtsAudioControls
                  text={stripHtml(option.text)}
                  audioUrl={option.audio}
                  onAudioChange={(url) =>
                    updateOption(option.id, { audio: url })
                  }
                  generateAudio={generateAudio}
                />
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeOption(option.id)}
                  disabled={options.length <= 2}
                />
              </Space>
              <div style={{ marginTop: 8 }}>
                <ImageUploadInput
                  value={option.image}
                  onChange={(url) => updateOption(option.id, { image: url })}
                  label="Imagen de la opción"
                />
              </div>
            </div>
          ))}
          <div style={{ marginTop: 8 }}>
            <Button
              icon={<PlusOutlined />}
              onClick={addOption}
              type="dashed"
              block
            >
              Agregar opción
            </Button>
          </div>
        </div>
      </Space>
    </div>
  );
};

export default MultipleChoiceEditor;
